
import { Component, toNative, Vue } from 'vue-facing-decorator';
import SideDrawer from '@/components/Common/SideDrawer.vue';
// import ModalPopup from '@/components/Common/ModalPopup.vue';
import { Root, Store, t } from '@/main';
import { EventAttendeeDto } from '@/entities/Participants/EventAttendeeDto';
import ParticipantGroupDto from '@/entities/Participants/ParticipantGroupDto';
import ParticipantNotesDto from '@/entities/Participants/ParticipantNotesDto';
import ModalPopup from '@/components/Common/ModalPopup.vue';
import { StoreHelper } from '@/store/StoreHelper';

interface ParticipantStats {
  totalInvitationsSent: number;
  registrationsComplete: number;
  joinedEvent: number;
  checkedIn: number;
}

interface AddParticipantForm {
  firstName: string;
  lastName: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  about: string;
  website: string;
  xHandle: string;
  instagram: string;
  linkedIn: string;
  facebook: string;
  note: string;
  groupIds: number[];
}

@Component({
  components: {
    SideDrawer,
    ModalPopup,
  },
})
class ParticipantPageHeader extends Vue {
  private readonly storeHelper = new StoreHelper(Store);

  private get enableRegistrationSystem(): boolean {
    return this.storeHelper.GetRequestInfoFromStore().EnableRegistrationSystem;
  }

  private readonly addIcon = require('@/assets/Images/white-add-icon.svg');
  private readonly dropDownIcon = require('@/assets/Images/down-arrow-white.svg');
  private readonly invitationSentIcon = require('@/assets/Images/Attendance/send-invite-icon.svg');
  private readonly registrationsIcon = require('@/assets/Images/Attendance/participants-registration-completion-icon.svg');
  private readonly joinedEventIcon = require('@/assets/Images/Attendance/participants-event-join-icon.svg');
  private readonly checkedInIcon = require('@/assets/Images/Attendance/checked-in-participant-icon.svg');
  private readonly privacyIcon = require('@/assets/Images/privacy-icon.svg');
  private readonly shareIcon = require('@/assets/Images/share-icon.svg');
  private readonly maxImportLimit = 5000;

  private modalPopupHeading = '';
  private modalPopupShowCancelButton = true;
  private modalPopupCancelButtonText = '';
  private modalPopupConfirmButtonText = '';
  private showCloseButton = false;

  private stats: ParticipantStats = {
    totalInvitationsSent: 0,
    registrationsComplete: 0,
    joinedEvent: 0,
    checkedIn: 0,
  };

  // ── Privacy drawer state ───────────────────────────────────────────
  private showPrivacyDrawer = false;
  private privacyDisplayInApp = false;
  private privacyHideEmails = false;

  // ── Privacy confirmation modal ─────────────────────────────────────
  private showPrivacyConfirmModal = false;
  private pendingPrivacyToggle: string | null = null;

  private get privacyConfirmBodyKey(): string {
    return this.pendingPrivacyToggle === 'displayInApp'
      ? this.$t('Participant.Privacy.ConfirmDisplayInAppBody').toString()
      : this.$t('Participant.Privacy.ConfirmHideEmailsBody').toString();
  }

  // ── Add participant drawer state ───────────────────────────────────
  private showAddDrawer = false;
  private addForm: AddParticipantForm = this.EmptyAddForm();
  private allGroups: ParticipantGroupDto[] = [];

  // ── Import drawer state ────────────────────────────────────────────
  private showImportDrawer = false;
  private importFile: File | null = null;

  // ── Lifecycle ──────────────────────────────────────────────────────
  private mounted() {
    Root.on('participant-stats-loaded', (payload: any) => {
      this.stats = {
        totalInvitationsSent: payload.totalInvitesSent            ?? 0,
        registrationsComplete: payload.totalCompletedRegistrations ?? 0,
        joinedEvent:           payload.totalJoinedParticipants     ?? 0,
        checkedIn:             payload.totalSelfCheckedIn          ?? 0,
      };
      this.privacyDisplayInApp = payload.displayParticipantsInApp   ?? false;
      this.privacyHideEmails   = payload.hideParticipantsEmailInApp ?? false;
      this.allGroups           = payload.allGroups                   ?? [];
    });
  }

  private beforeUnmount() {
    Root.off('participant-stats-loaded');
  }

  // ── Tooltip state ──────────────────────────────────────────────────
  private tooltips = {
    invitationsSent: false,
    registrations: false,
    joinedEvent: false,
    checkedIn: false,
    customFields: false,
    addNotes: false,
  };

  private ShowTooltip(key: string) {
    (this.tooltips as any)[key] = true;
  }

  private HideTooltip(key: string) {
    (this.tooltips as any)[key] = false;
  }

  private EmptyAddForm(): AddParticipantForm {
    return {
      firstName: '', lastName: '', title: '', company: '',
      email: '', phone: '', about: '', website: '',
      xHandle: '', instagram: '', linkedIn: '', facebook: '',
      note: '', groupIds: [],
    };
  }

  // ── Privacy drawer ─────────────────────────────────────────────────
  private OpenPrivacyDrawer() {
    this.showPrivacyDrawer = true;
  }

  private ClosePrivacyDrawer() {
    this.showPrivacyDrawer = false;
    // TODO: FetchData('/Participant/UpdatePrivacy', JSON.stringify({ displayInApp: this.privacyDisplayInApp, hideEmails: this.privacyHideEmails }))
  }

  private RequestPrivacyToggle(field: string) {
    this.pendingPrivacyToggle = field;
    this.showPrivacyConfirmModal = true;
  }

  private OnPrivacyConfirm(confirmed: boolean) {
    this.showPrivacyConfirmModal = false;
    if (!confirmed) {
      this.pendingPrivacyToggle = null;
      return;
    }
    if (this.pendingPrivacyToggle === 'displayInApp') {
      this.privacyDisplayInApp = !this.privacyDisplayInApp;
    } else if (this.pendingPrivacyToggle === 'hideEmails') {
      this.privacyHideEmails = !this.privacyHideEmails;
    }
    this.pendingPrivacyToggle = null;
    // TODO: FetchData('/Participant/UpdatePrivacy', JSON.stringify({ displayInApp: this.privacyDisplayInApp, hideEmails: this.privacyHideEmails }))
  }

  // ── Add participant drawer ─────────────────────────────────────────
  private OpenAddDrawer() {
    this.addForm = this.EmptyAddForm();
    this.showAddDrawer = true;
  }

  private ToggleAddGroup(groupId: number) {
    const idx = this.addForm.groupIds.indexOf(groupId);
    if (idx === -1) {
      this.addForm.groupIds.push(groupId);
    } else {
      this.addForm.groupIds.splice(idx, 1);
    }
  }

  private ConfirmAddParticipant() {
    const dto = new EventAttendeeDto();
    dto.FirstName               = this.addForm.firstName;
    dto.LastName                = this.addForm.lastName;
    dto.Email                   = this.addForm.email;
    dto.Phone                   = this.addForm.phone;
    dto.Title                   = this.addForm.title;
    dto.Company                 = this.addForm.company;
    dto.About                   = this.addForm.about;
    dto.Website                 = this.addForm.website;
    dto.TwitterHandle           = this.addForm.xHandle;
    dto.Instagram               = this.addForm.instagram;
    dto.LinkedInPublicProfileUrl = this.addForm.linkedIn;
    dto.FacebookProfileId       = this.addForm.facebook;
    dto.GroupIds                = [...this.addForm.groupIds];
    if (this.addForm.note.trim()) {
      const note = new ParticipantNotesDto();
      note.Note = this.addForm.note;
      dto.ParticipantNotes = [note];
    }
    console.log('Add participant payload:', dto);
    this.showAddDrawer = false;
    // TODO: FetchData('/Participant/Add', JSON.stringify(dto))
  }

  // ── Import drawer ──────────────────────────────────────────────────
  private OpenImportDrawer() {
    this.importFile = null;
    this.showImportDrawer = true;
  }

  private TriggerFileInput() {
    (this.$refs.fileInput as HTMLInputElement).click();
  }

  private OnFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.importFile = input.files[0];
    }

  }

  private OnFileDrop(event: DragEvent) {
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (file.name.endsWith('.xlsx')) {
        this.importFile = file;
      }
    }
  }

  private ConfirmImport() {
    this.showImportDrawer = false;
    // TODO: upload importFile via FormData + FetchData
  }
}

export default toNative(ParticipantPageHeader);
